import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { GoogleButton } from "./LoginPage";

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form.name, form.email, form.password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="register-page">
      <h1 className="text-3xl font-bold tracking-tight">Create account</h1>
      <p className="mt-2 text-sm text-muted-foreground">Save delivery addresses and track your orders.</p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4 rounded-sm border border-border bg-white p-6">
        {error && (
          <div className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password (min 6 characters)</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-secondary disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </button>
        <div className="relative py-1 text-center text-xs text-muted-foreground">
          <span className="relative z-10 bg-white px-2">or</span>
          <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
        </div>
        <GoogleButton onClick={loginWithGoogle} />
      </form>

      <p className="mt-4 text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-secondary hover:underline" data-testid="go-to-login-link">
          Sign in
        </Link>
      </p>
    </div>
  );
}
